/**
 * MP3-based background music player using HTMLAudioElement.
 *
 * Plays a shuffled playlist of tracks. When a track ends it crossfades
 * into the next one. The playlist reshuffles when all tracks have played.
 *
 * HTMLAudioElement is more reliable than Web Audio API for long-form music
 * playback — handles codec quirks and browser restrictions better.
 * Crossfade is done via volume interpolation.
 */

const PLAYLIST = [
  '/audio/music/dark-atmosphere-to-synth.mp3',
  '/audio/music/dystopian.mp3',
  '/audio/music/empty-streets.mp3',
  '/audio/music/mystery-unsolved.mp3',
  '/audio/music/surveillance.mp3',
  '/audio/music/the-nightclub.mp3',
  '/audio/music/the-protagonist.mp3',
  '/audio/music/the-story-continues.mp3',
];

const BASE_VOLUME = 0.25;
const FADE_MS = 1200;
const FADE_STEP_MS = 30;

/** Fisher-Yates shuffle (in-place). */
function shuffle<T>(arr: T[]): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

export class Music {
  private current: HTMLAudioElement | null = null;
  private fadeTimer: ReturnType<typeof setInterval> | null = null;
  private order: string[] = [];
  private index = 0;
  private playing = false;
  muted = false;
  private volume = 1;

  /** Pre-create Audio elements and start loading first tracks. */
  preload(): void {
    if (this.order.length === 0) this.reshuffle();
    // Preload first two tracks so playback starts instantly
    for (let i = 0; i < Math.min(2, this.order.length); i++) {
      const a = new Audio(this.order[i]);
      a.preload = 'auto';
    }
  }

  /** Resume playback after user gesture (browsers may block autoplay). */
  resume(): void {
    if (this.playing && this.current?.paused) {
      void this.current.play();
    }
  }

  /** Start playing the shuffled playlist from a random position. */
  play(): void {
    if (this.playing) return;
    this.playing = true;
    this.reshuffle();
    this.playTrack(this.order[this.index]);
  }

  setMuted(m: boolean): void {
    this.muted = m;
    this.applyVolume();
  }

  setVolume(v: number): void {
    this.volume = Math.max(0, Math.min(1, v));
    this.applyVolume();
  }

  stop(): void {
    this.playing = false;
    this.clearFade();
    if (this.current) {
      this.current.pause();
      this.current.removeEventListener('ended', this.onTrackEnd);
      this.current = null;
    }
  }

  private effectiveVolume(): number {
    return this.muted ? 0 : BASE_VOLUME * this.volume;
  }

  private applyVolume(): void {
    if (!this.current) return;
    this.clearFade();
    this.current.volume = this.effectiveVolume();
  }

  private reshuffle(): void {
    this.order = shuffle([...PLAYLIST]);
    this.index = 0;
  }

  private advance(): void {
    this.index++;
    if (this.index >= this.order.length) {
      this.reshuffle();
    }
    this.playTrack(this.order[this.index]);
  }

  private onTrackEnd = (): void => {
    if (this.playing) this.advance();
  };

  private playTrack(url: string): void {
    const old = this.current;

    // Fade out old track
    if (old) {
      old.removeEventListener('ended', this.onTrackEnd);
      this.fadeVolume(old, 0, () => {
        old.pause();
      });
    }

    // Create and start new track
    const el = new Audio(url);
    el.preload = 'auto';
    el.volume = 0;
    el.addEventListener('ended', this.onTrackEnd);
    this.current = el;

    void el.play().catch(() => {
      // Autoplay blocked — will be retried via resume() on user gesture
    });

    // Fade in new track (after a small delay to let old fade start)
    const targetVol = this.effectiveVolume();
    if (old) {
      setTimeout(() => this.fadeVolume(el, targetVol), FADE_MS);
    } else {
      this.fadeVolume(el, targetVol);
    }
  }

  private clearFade(): void {
    if (this.fadeTimer) {
      clearInterval(this.fadeTimer);
      this.fadeTimer = null;
    }
  }

  private fadeVolume(
    el: HTMLAudioElement,
    target: number,
    onDone?: () => void,
  ): void {
    // Only clear fade timer for the current element
    if (el === this.current) this.clearFade();

    const steps = Math.ceil(FADE_MS / FADE_STEP_MS);
    const startVol = el.volume;
    const delta = (target - startVol) / steps;
    let step = 0;

    const timer = setInterval(() => {
      step++;
      if (step >= steps) {
        el.volume = target;
        clearInterval(timer);
        if (el === this.current) this.fadeTimer = null;
        onDone?.();
      } else {
        el.volume = Math.max(0, Math.min(1, startVol + delta * step));
      }
    }, FADE_STEP_MS);

    if (el === this.current) this.fadeTimer = timer;
  }
}
